/**
 * The player's arithmetic: speeds, frames, the A-B loop and where one clip sits among
 * the movement's others. No element and no timer — the lib hooks own those.
 */

import { MS_PER_DAY } from '~/shared/lib/calendarDate';

export const PLAYBACK_RATES = [0.25, 0.5, 1, 1.5, 2] as const;
export type PlaybackRate = (typeof PLAYBACK_RATES)[number];

/** One step slower or faster, stopping at the ends rather than wrapping. */
export function stepRate(current: number, by: 1 | -1): PlaybackRate {
  const index = PLAYBACK_RATES.findIndex((rate) => rate === current);
  const from = index === -1 ? PLAYBACK_RATES.indexOf(1) : index;
  const next = Math.max(0, Math.min(PLAYBACK_RATES.length - 1, from + by));
  return PLAYBACK_RATES[next] ?? 1;
}

/** When the browser cannot report a frame's length, the film is assumed to run at 30 fps. */
export const FALLBACK_FRAME_SECONDS = 1 / 30;

/**
 * One frame's length from two presented frames the video frame callback reported. Anything that
 * does not describe a real frame (a pause, a seek, a dropped callback) keeps the previous answer.
 */
export function frameSeconds(
  previous: { readonly mediaTime: number; readonly presentedFrames: number } | null,
  next: { readonly mediaTime: number; readonly presentedFrames: number },
  current: number,
): number {
  if (previous === null) return current;
  const frames = next.presentedFrames - previous.presentedFrames;
  const elapsed = next.mediaTime - previous.mediaTime;
  if (frames !== 1 || elapsed <= 0 || elapsed > 0.2) return current;
  return elapsed;
}

export const clampTime = (seconds: number, duration: number): number =>
  Math.max(0, Math.min(Number.isFinite(duration) && duration > 0 ? duration : seconds, seconds));

export interface LoopRange {
  readonly from: number | null;
  readonly to: number | null;
}

export const NO_LOOP: LoopRange = { from: null, to: null };

/** `[` — the loop starts here. An end already set before this point is dropped. */
export function loopFrom(range: LoopRange, at: number): LoopRange {
  return { from: at, to: range.to !== null && range.to > at ? range.to : null };
}

/** `]` — the loop ends here. With no start yet the loop runs from the beginning. */
export function loopTo(range: LoopRange, at: number): LoopRange {
  const from = range.from ?? 0;
  return at > from ? { from, to: at } : range;
}

/** Where a looping film goes next: back to the start of the range once it has passed the end. */
export function loopedTime(range: LoopRange, at: number): number | null {
  if (range.from === null || range.to === null) return null;
  return at >= range.to || at < range.from ? range.from : null;
}

/** Wraps round the list, like YouTube's playlist. */
export function neighbourIndex(index: number, by: 1 | -1, length: number): number {
  if (length === 0) return -1;
  return (((index + by) % length) + length) % length;
}

/** The next clip after `index` that is unread, wrapping round, or `null` when no other one is. */
export function nextUnreadIndex(unread: readonly boolean[], index: number): number | null {
  for (let step = 1; step < unread.length; step += 1) {
    const at = (index + step) % unread.length;
    if (unread[at] === true) return at;
  }
  return null;
}

/** A pin this close to the playhead is the one already standing there, not the next one. */
const PIN_TOLERANCE = 0.05;

/** The comment pin to jump to from `at`, in either direction, or `null` at the ends. */
export function pinJump(pins: readonly number[], at: number, by: 1 | -1): number | null {
  const sorted = [...pins].sort((left, right) => left - right);
  if (by === 1) return sorted.find((pin) => pin > at + PIN_TOLERANCE) ?? null;
  return [...sorted].reverse().find((pin) => pin < at - PIN_TOLERANCE) ?? null;
}

/**
 * The moment a new comment is pinned to, in the wire's milliseconds: the film's time when the
 * trainer sends it, or `null` for a remark about the whole clip: one unpinned by hand, or one
 * written beside a film nobody has started, whose playhead stands at 0:00 by default, not by choice.
 */
export function commentOffsetMs(pinned: boolean, seconds: number): number | null {
  return pinned ? Math.round(Math.max(0, seconds) * 1000) : null;
}

/**
 * The second the full-screen player opens at from the side panel's small one: where the trainer
 * left the film, or the top when it was never started or stands at the start anyway.
 */
export function openingSecond(started: boolean, seconds: number): number | null {
  return started && seconds > 0 ? seconds : null;
}

/** Where the zoomed film is anchored, in percent of its box on each axis. */
export interface ZoomOrigin {
  readonly x: number;
  readonly y: number;
}

export const ZOOM_CENTRE: ZoomOrigin = { x: 50, y: 50 };

const PAN_STEP = 10;

/** One arrow press while zoomed: a tenth of the film towards the arrow, never past its edge. */
export function panOrigin(origin: ZoomOrigin, dx: -1 | 0 | 1, dy: -1 | 0 | 1): ZoomOrigin {
  const clamp = (value: number) => Math.max(0, Math.min(100, value));
  return { x: clamp(origin.x + dx * PAN_STEP), y: clamp(origin.y + dy * PAN_STEP) };
}

/** Whole days until an instant, for "Zniknie za 10 dni". Never below one while it still exists. */
export function daysUntil(at: number, now: number): number {
  return Math.max(1, Math.ceil((at - now) / MS_PER_DAY));
}
